import Alpine, {Magics} from 'alpinejs';
import type {CartLinesUpdateEvent, CartNoteUpdateEvent} from '@shopify/events';
import {requestIdleCallback} from '@theme/utils';

type CartApiSectionComponent = Magics<any> & {
  refresh: () => void;
  applySections: (sections?: Record<string, string>) => void;
};

Alpine.directive('cart-api-section', ($el) => {
  Alpine.bind($el, {
    'x-data'(this: Magics<any>) {
      return {
        refresh: Alpine.debounce(() => {
          this.$sectionApi.update({batch: true, cachePolicy: 'network-only'});
        }, 500),

        // Morph this section from the cart response's sections HTML, or fall
        // back to a separate section rendering request when it is missing
        applySections(this: CartApiSectionComponent, sections?: Record<string, string>) {
          const updatedSectionHtml = this.$sectionId ? sections?.[this.$sectionId] : undefined;

          if (typeof updatedSectionHtml === 'string') {
            this.$sectionApi.setHtml(updatedSectionHtml, {mode: 'morph'});
          } else {
            this.refresh();
          }
        },
      };
    },
    // Mark this section so that it's id is included in the 'sections' parameter of the cart API requests
    'data-cart-api-section': 'true',

    // The standard cart events fire before their request starts; the sections
    // HTML arrives when the event's promise resolves
    // https://shopify.dev/docs/api/storefront-events-and-actions
    async '@shopify:cart:lines-update.window'(this: CartApiSectionComponent) {
      try {
        const {detail} = await ((this as any).$event as CartLinesUpdateEvent).promise;

        if (detail?.didError) {
          // shopify:cart:error already triggered a refresh
          return;
        }

        this.applySections(detail?.sections);
      } catch {
        this.refresh();
      }
    },

    async '@shopify:cart:note-update.window'(this: CartApiSectionComponent) {
      try {
        const {detail} = await ((this as any).$event as CartNoteUpdateEvent).promise;

        if ((detail as any)?.didError) {
          return;
        }

        this.applySections((detail as any)?.sections);
      } catch {
        this.refresh();
      }
    },

    '@shopify:cart:error.window'(this: CartApiSectionComponent) {
      this.refresh();
    },

    // Refresh the section when the page tab becomes active to ensure the cart data is correct when switching between tabs.
    '@visibilitychange.window'(this: CartApiSectionComponent) {
      requestIdleCallback(
        () => {
          if (document.hidden) return;
          this.refresh();
        },
        {timeout: 5000},
      );
    },
  });
});
